import type { ReactNode } from 'react';

type Tone = 'neutral' | 'success' | 'danger' | 'warning' | 'info' | 'sky';

// Badges use brand/semantic hues as backgrounds (a fill use, so brand values are fine).
const TONES: Record<Tone, string> = {
  neutral: 'bg-frost text-navy-700',
  success: 'bg-green-600/12 text-green-700',
  danger: 'bg-danger/12 text-danger',
  warning: 'bg-warning/12 text-warning',
  info: 'bg-info/12 text-info',
  sky: 'bg-sky-500/12 text-sky-ink',
};

const COMPACT_TONES: Record<Tone, string> = {
  neutral: 'border-frost bg-cloud text-navy-700',
  success: 'border-green-600/25 bg-green-600/5 text-green-700',
  danger: 'border-danger/25 bg-danger/5 text-danger',
  warning: 'border-warning/25 bg-warning/5 text-warning',
  info: 'border-info/25 bg-info/5 text-info',
  sky: 'border-sky-500/25 bg-sky-500/5 text-sky-ink',
};

export function Badge({
  children,
  tone = 'neutral',
  className = '',
  compact = false,
}: {
  children: ReactNode;
  tone?: Tone;
  className?: string;
  compact?: boolean;
}): JSX.Element {
  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${compact ? `gap-1 border px-2 py-0.5 text-[11px] leading-[14px] ${COMPACT_TONES[tone]}` : `px-2.5 py-0.5 text-[11px] tracking-wide ${TONES[tone]}`} ${className}`}
    >
      {compact && <span className="h-1 w-1 shrink-0 rounded-full bg-current" aria-hidden />}
      {children}
    </span>
  );
}

/** Maps stock level to the low-stock / out-of-stock badge (SRS §4.3, App. A). */
export function StockBadge({
  stock,
  threshold = 5,
  compact = false,
}: {
  stock: number;
  threshold?: number;
  compact?: boolean;
}): JSX.Element {
  if (stock <= 0)
    return (
      <Badge tone="danger" compact={compact}>
        Out of stock
      </Badge>
    );
  if (stock <= threshold)
    return (
      <Badge tone="warning" compact={compact}>
        Only {stock} left
      </Badge>
    );
  return (
    <Badge tone="success" compact={compact}>
      In stock
    </Badge>
  );
}
